// src/distance.ts
var EARTH_RADIUS_M = 63710088e-1;
var toRad = (deg) => deg * Math.PI / 180;
var toDeg = (rad) => rad * 180 / Math.PI;
function haversineMeters(a, b) {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}
function bearingDegrees(a, b) {
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const dLng = toRad(b.lng - a.lng);
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}
function destinationPoint(start, bearingDeg, distanceM) {
  const delta = distanceM / EARTH_RADIUS_M;
  const theta = toRad(bearingDeg);
  const lat1 = toRad(start.lat);
  const lng1 = toRad(start.lng);
  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(delta) + Math.cos(lat1) * Math.sin(delta) * Math.cos(theta)
  );
  const lng2 = lng1 + Math.atan2(
    Math.sin(theta) * Math.sin(delta) * Math.cos(lat1),
    Math.cos(delta) - Math.sin(lat1) * Math.sin(lat2)
  );
  return { lat: toDeg(lat2), lng: (toDeg(lng2) + 540) % 360 - 180 };
}
function interpolate(a, b, t) {
  return { lat: a.lat + (b.lat - a.lat) * t, lng: a.lng + (b.lng - a.lng) * t };
}
function polylineLengthMeters(points) {
  let total = 0;
  for (let i = 1; i < points.length; i++) total += haversineMeters(points[i - 1], points[i]);
  return total;
}
function paceSecondsPerKm(distanceM, durationS) {
  if (distanceM < 10 || durationS <= 0) return null;
  return durationS / (distanceM / 1e3);
}

// src/point.ts
function isValidCoordinate(p) {
  if (!p) return false;
  const { lat, lng } = p;
  return typeof lat === "number" && typeof lng === "number" && Number.isFinite(lat) && Number.isFinite(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180 && // (0,0) "Null Island" is the classic sentinel of a broken GPS fix.
  !(lat === 0 && lng === 0);
}

// src/polygon.ts
function pointInRing(p, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const intersects = yi > p.lat !== yj > p.lat && p.lng < (xj - xi) * (p.lat - yi) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}
function pointInPolygon(p, polygon) {
  const [outer, ...holes] = polygon.coordinates;
  if (!outer || !pointInRing(p, outer)) return false;
  return !holes.some((h) => pointInRing(p, h));
}
function polygonBBox(polygon) {
  const outer = polygon.coordinates[0] ?? [];
  const bbox = { minLat: Infinity, minLng: Infinity, maxLat: -Infinity, maxLng: -Infinity };
  for (const [lng, lat] of outer) {
    bbox.minLat = Math.min(bbox.minLat, lat);
    bbox.maxLat = Math.max(bbox.maxLat, lat);
    bbox.minLng = Math.min(bbox.minLng, lng);
    bbox.maxLng = Math.max(bbox.maxLng, lng);
  }
  return bbox;
}
function bboxContains(b, p) {
  return p.lat >= b.minLat && p.lat <= b.maxLat && p.lng >= b.minLng && p.lng <= b.maxLng;
}
function bboxIntersects(a, b) {
  return !(a.maxLat < b.minLat || a.minLat > b.maxLat || a.maxLng < b.minLng || a.minLng > b.maxLng);
}
function bboxCenter(b) {
  return { lat: (b.minLat + b.maxLat) / 2, lng: (b.minLng + b.maxLng) / 2 };
}
var polygonsOf = (area) => area.type === "Polygon" ? [area.coordinates] : area.coordinates;
function pointInArea(p, area) {
  return polygonsOf(area).some((coordinates) => pointInPolygon(p, { type: "Polygon", coordinates }));
}
function areaBBox(area) {
  const bbox = { minLat: Infinity, minLng: Infinity, maxLat: -Infinity, maxLng: -Infinity };
  for (const poly of polygonsOf(area)) {
    const b = polygonBBox({ type: "Polygon", coordinates: poly });
    bbox.minLat = Math.min(bbox.minLat, b.minLat);
    bbox.maxLat = Math.max(bbox.maxLat, b.maxLat);
    bbox.minLng = Math.min(bbox.minLng, b.minLng);
    bbox.maxLng = Math.max(bbox.maxLng, b.maxLng);
  }
  return bbox;
}
var AreaIndex = class {
  bbox;
  bands;
  bandH;
  constructor(area, bands = 512) {
    this.bbox = areaBBox(area);
    this.bandH = (this.bbox.maxLat - this.bbox.minLat) / bands || 1;
    this.bands = Array.from({ length: bands }, () => []);
    for (const poly of polygonsOf(area)) {
      for (const ring of poly) {
        for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
          const [x1, y1] = ring[j];
          const [x2, y2] = ring[i];
          if (y1 === y2) continue;
          const from = this.band(Math.min(y1, y2));
          const to = this.band(Math.max(y1, y2));
          for (let b = from; b <= to; b++) this.bands[b].push([x1, y1, x2, y2]);
        }
      }
    }
  }
  band(lat) {
    return Math.max(0, Math.min(this.bands.length - 1, Math.floor((lat - this.bbox.minLat) / this.bandH)));
  }
  contains(p) {
    if (!bboxContains(this.bbox, p)) return false;
    let inside = false;
    for (const [x1, y1, x2, y2] of this.bands[this.band(p.lat)]) {
      if (y1 > p.lat !== y2 > p.lat && p.lng < (x2 - x1) * (p.lat - y1) / (y2 - y1) + x1) inside = !inside;
    }
    return inside;
  }
};

// src/projection.ts
var LocalProjection = class {
  constructor(origin) {
    this.origin = origin;
    const phi = origin.lat * Math.PI / 180;
    this.mPerDegLat = 111132.92 - 559.82 * Math.cos(2 * phi) + 1.175 * Math.cos(4 * phi);
    this.mPerDegLng = 111412.84 * Math.cos(phi) - 93.5 * Math.cos(3 * phi);
  }
  origin;
  mPerDegLat;
  mPerDegLng;
  toXY(p) {
    return {
      x: (p.lng - this.origin.lng) * this.mPerDegLng,
      y: (p.lat - this.origin.lat) * this.mPerDegLat
    };
  }
  toLatLng(p) {
    return {
      lat: this.origin.lat + p.y / this.mPerDegLat,
      lng: this.origin.lng + p.x / this.mPerDegLng
    };
  }
};

// src/hexgrid.ts
var SQRT3 = Math.sqrt(3);
var AXIAL_DIRECTIONS = [
  [1, 0],
  [1, -1],
  [0, -1],
  [-1, 0],
  [-1, 1],
  [0, 1]
];
function cellKey(q, r) {
  return `${q === 0 ? 0 : q}:${r === 0 ? 0 : r}`;
}
function parseCellKey(key) {
  const m = /^(-?\d+):(-?\d+)$/.exec(key);
  if (!m) throw new Error(`Invalid cell key: ${key}`);
  return { q: Number(m[1]), r: Number(m[2]) };
}
var HexGrid = class {
  constructor(config) {
    this.config = config;
    if (!(config.sizeMeters > 0)) throw new Error("sizeMeters must be > 0");
    this.projection = new LocalProjection({ lat: config.originLat, lng: config.originLng });
    this.radius = config.sizeMeters / SQRT3;
  }
  config;
  projection;
  /** Circumradius (centre → corner) in meters. */
  radius;
  cellAt(p) {
    const { x, y } = this.projection.toXY(p);
    const qf = (SQRT3 / 3 * x - 1 / 3 * y) / this.radius;
    const rf = 2 / 3 * y / this.radius;
    const { q, r } = cubeRound(qf, rf);
    return { q, r, key: cellKey(q, r) };
  }
  keyAt(p) {
    return this.cellAt(p).key;
  }
  centerOf(key) {
    const { q, r } = parseCellKey(key);
    return this.projection.toLatLng({
      x: this.radius * SQRT3 * (q + r / 2),
      y: this.radius * 1.5 * r
    });
  }
  /** Closed polygon ring in GeoJSON [lng, lat] order. */
  polygonOf(key) {
    const { q, r } = parseCellKey(key);
    const cx = this.radius * SQRT3 * (q + r / 2);
    const cy = this.radius * 1.5 * r;
    const ring = [];
    for (let i = 0; i < 6; i++) {
      const angle = (60 * i - 30) * Math.PI / 180;
      const ll = this.projection.toLatLng({
        x: cx + this.radius * Math.cos(angle),
        y: cy + this.radius * Math.sin(angle)
      });
      ring.push([ll.lng, ll.lat]);
    }
    ring.push(ring[0]);
    return ring;
  }
  neighbors(key) {
    const { q, r } = parseCellKey(key);
    return AXIAL_DIRECTIONS.map(([dq, dr]) => cellKey(q + dq, r + dr));
  }
  /** Hex distance (number of steps) between two cells. */
  distance(a, b) {
    const A = parseCellKey(a);
    const B = parseCellKey(b);
    const dq = A.q - B.q;
    const dr = A.r - B.r;
    return (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2;
  }
  /** All cells whose centre lies inside the bbox. */
  cellsInBBox(bbox, limit = 5e4) {
    const sw = this.projection.toXY({ lat: bbox.minLat, lng: bbox.minLng });
    const ne = this.projection.toXY({ lat: bbox.maxLat, lng: bbox.maxLng });
    const rowH = this.radius * 1.5;
    const colW = this.radius * SQRT3;
    const rMin = Math.floor(sw.y / rowH) - 1;
    const rMax = Math.ceil(ne.y / rowH) + 1;
    const out = [];
    for (let r = rMin; r <= rMax; r++) {
      const qMin = Math.floor(sw.x / colW - r / 2) - 1;
      const qMax = Math.ceil(ne.x / colW - r / 2) + 1;
      for (let q = qMin; q <= qMax; q++) {
        const key = cellKey(q, r);
        const c = this.centerOf(key);
        if (c.lat >= bbox.minLat && c.lat <= bbox.maxLat && c.lng >= bbox.minLng && c.lng <= bbox.maxLng) {
          out.push(key);
          if (out.length >= limit) return out;
        }
      }
    }
    return out;
  }
};
function cubeRound(qf, rf) {
  const sf = -qf - rf;
  let q = Math.round(qf);
  let r = Math.round(rf);
  const s = Math.round(sf);
  const dq = Math.abs(q - qf);
  const dr = Math.abs(r - rf);
  const ds = Math.abs(s - sf);
  if (dq > dr && dq > ds) q = -r - s;
  else if (dr > ds) r = -q - s;
  return { q, r };
}

// src/route.ts
function segmentCellDistances(grid, a, b, stepMeters = 5) {
  const out = /* @__PURE__ */ new Map();
  const length = haversineMeters(a, b);
  if (length === 0) return out;
  const steps = Math.max(1, Math.ceil(length / stepMeters));
  const piece = length / steps;
  for (let i = 0; i < steps; i++) {
    const mid = interpolate(a, b, (i + 0.5) / steps);
    const key = grid.keyAt(mid);
    out.set(key, (out.get(key) ?? 0) + piece);
  }
  return out;
}
function cellsAlongRoute(grid, points, stepMeters = 5) {
  const seen = [];
  const push = (k) => {
    if (seen[seen.length - 1] !== k) seen.push(k);
  };
  if (points[0]) push(grid.keyAt(points[0]));
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    const steps = Math.max(1, Math.ceil(haversineMeters(a, b) / stepMeters));
    for (let s = 1; s <= steps; s++) push(grid.keyAt(interpolate(a, b, s / steps)));
  }
  return seen;
}
function simplifyRoute(points, toleranceM) {
  if (points.length <= 2) return [...points];
  const keep = new Uint8Array(points.length);
  keep[0] = 1;
  keep[points.length - 1] = 1;
  const stack = [[0, points.length - 1]];
  while (stack.length) {
    const [s, e] = stack.pop();
    let maxD = 0;
    let idx = -1;
    for (let i = s + 1; i < e; i++) {
      const d = perpendicularDistanceM(points[i], points[s], points[e]);
      if (d > maxD) {
        maxD = d;
        idx = i;
      }
    }
    if (idx !== -1 && maxD > toleranceM) {
      keep[idx] = 1;
      stack.push([s, idx], [idx, e]);
    }
  }
  return points.filter((_, i) => keep[i]);
}
function perpendicularDistanceM(p, a, b) {
  const kx = 111320 * Math.cos(a.lat * Math.PI / 180);
  const ky = 110574;
  const ax = 0;
  const ay = 0;
  const bx = (b.lng - a.lng) * kx;
  const by = (b.lat - a.lat) * ky;
  const px = (p.lng - a.lng) * kx;
  const py = (p.lat - a.lat) * ky;
  const len2 = bx * bx + by * by;
  if (len2 === 0) return Math.hypot(px - ax, py - ay);
  const t = Math.max(0, Math.min(1, (px * bx + py * by) / len2));
  return Math.hypot(px - t * bx, py - t * by);
}
function trimRouteEnds(lines, meters) {
  const flatLen = lines.reduce((s, l) => s + lineLength(l), 0);
  if (flatLen <= meters * 2) return [];
  const trimmedStart = trimFront(lines.map((l) => [...l]), meters);
  const reversed = trimmedStart.map((l) => [...l].reverse()).reverse();
  return trimFront(reversed, meters).map((l) => l.reverse()).reverse();
}
function lineLength(l) {
  let d = 0;
  for (let i = 1; i < l.length; i++) d += haversineMeters(toLL(l[i - 1]), toLL(l[i]));
  return d;
}
var toLL = (p) => ({ lng: p[0], lat: p[1] });
function trimFront(lines, meters) {
  let left = meters;
  const out = [];
  for (const line of lines) {
    if (left <= 0) {
      out.push(line);
      continue;
    }
    let i = 1;
    let cut = null;
    for (; i < line.length; i++) {
      const a = line[i - 1];
      const b = line[i];
      const d = haversineMeters(toLL(a), toLL(b));
      if (d >= left) {
        const t = d === 0 ? 0 : left / d;
        const p = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
        cut = [p, ...line.slice(i)];
        left = 0;
        break;
      }
      left -= d;
    }
    if (cut && cut.length >= 2) out.push(cut);
  }
  return out;
}
export {
  AXIAL_DIRECTIONS,
  AreaIndex,
  EARTH_RADIUS_M,
  HexGrid,
  LocalProjection,
  areaBBox,
  bboxCenter,
  bboxContains,
  bboxIntersects,
  bearingDegrees,
  cellKey,
  cellsAlongRoute,
  destinationPoint,
  haversineMeters,
  interpolate,
  isValidCoordinate,
  paceSecondsPerKm,
  parseCellKey,
  pointInArea,
  pointInPolygon,
  pointInRing,
  polygonBBox,
  polylineLengthMeters,
  segmentCellDistances,
  simplifyRoute,
  trimRouteEnds
};
//# sourceMappingURL=index.js.map